import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

// ── Ticket status config ──────────────────────────────────────────────────────
const STATUS = {
  open:        { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6', label: 'Open'        },
  in_progress: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'In Progress' },
  resolved:    { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Resolved'    },
  closed:      { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: 'Closed'      },
}
const ts = (s) => STATUS[s?.toLowerCase()] ?? STATUS.open

const PRIORITY = {
  low:    { color: '#15803d', bg: '#f0fdf4', label: 'Low'    },
  medium: { color: '#b45309', bg: '#fffbeb', label: 'Medium' },
  high:   { color: '#b91c1c', bg: '#fef2f2', label: 'High'   },
  urgent: { color: '#7c3aed', bg: '#f5f3ff', label: 'Urgent' },
}
const pr = (p) => PRIORITY[p?.toLowerCase()] ?? PRIORITY.medium

const CATEGORIES = ['Account', 'Order', 'Payment', 'Delivery', 'Product', 'Shop', 'Technical', 'Other']

// ── Shared ────────────────────────────────────────────────────────────────────
const SS = {
  select: { border:'1px solid #e9e9e4', borderRadius:7, padding:'8px 12px', fontSize:12, fontWeight:600, color:'#111317', background:'#fff', cursor:'pointer', outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  input:  { width:'100%', height:42, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 13px', fontSize:13, fontWeight:500, color:'#111317', outline:'none', background:'#fff', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box' },
  label:  { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:"'JetBrains Mono',monospace" },
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 18px', flex:1 }}>
      <div style={{ fontSize:10, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:7 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:800, letterSpacing:'-0.5px', color:accent||'#111317' }}>{value}</div>
    </div>
  )
}

function StatusBadge({ status }) {
  const s = ts(status)
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:s.bg, color:s.color, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", padding:'3px 8px', borderRadius:4, textTransform:'uppercase', whiteSpace:'nowrap' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:s.dot }} />{s.label}
    </span>
  )
}

function PriorityBadge({ priority }) {
  const p = pr(priority)
  return (
    <span style={{ display:'inline-flex', alignItems:'center', background:p.bg, color:p.color, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", padding:'2px 7px', borderRadius:4, textTransform:'uppercase' }}>
      {p.label}
    </span>
  )
}

// ── FAQ Modal ─────────────────────────────────────────────────────────────────
function FaqModal({ faq, onClose, onSaved }) {
  const [form, setForm] = useState({ question: faq?.question||'', answer: faq?.answer||'', category: faq?.category||'General', active: faq?.active??true })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k,v) => setForm(f => ({...f, [k]: v}))

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    try {
      const res = faq?.faq_id
        ? await api.put(`/helpcenter/admin/faqs/${faq.faq_id}`, form)
        : await api.post('/helpcenter/admin/faqs', form)
      onSaved(res.data.faq)
      onClose()
    } catch (err) { setError(err.response?.data?.error || 'Failed to save') }
    finally { setLoading(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.22)', zIndex:300 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:540, maxHeight:'92vh', overflowY:'auto', background:'#fff', borderRadius:14, zIndex:301, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', animation:'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:15, fontWeight:800 }}>{faq?.faq_id ? 'Edit FAQ' : 'New FAQ'}</div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94' }}>×</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding:'18px 22px', display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={SS.label}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...SS.input, cursor:'pointer' }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={SS.label}>Question *</label>
              <input required value={form.question} onChange={e => set('question', e.target.value)} placeholder="What is the question?" style={SS.input} />
            </div>
            <div>
              <label style={SS.label}>Answer *</label>
              <textarea required value={form.answer} onChange={e => set('answer', e.target.value)} rows={5}
                placeholder="Write the answer here…"
                style={{ ...SS.input, height:'auto', padding:'10px 13px', resize:'vertical', lineHeight:1.6 }} />
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#fafaf8', borderRadius:8, border:'1px solid #e9e9e4' }}>
              <div style={{ fontSize:13, fontWeight:700 }}>Show to users</div>
              <div onClick={() => set('active', !form.active)}
                style={{ width:44, height:24, borderRadius:12, background:form.active?'#e8440a':'#e9e9e4', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
                <div style={{ position:'absolute', top:3, left:form.active?22:3, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.2s' }} />
              </div>
            </div>
            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </div>
          <div style={{ padding:'12px 22px 20px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10 }}>
            <button type="button" onClick={onClose} style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex:1, height:42, borderRadius:8, border:'none', background:loading?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:loading?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : faq?.faq_id ? 'Save Changes' : 'Create FAQ'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Ticket Drawer ─────────────────────────────────────────────────────────────
function TicketDrawer({ ticket, onClose, onUpdated }) {
  const [reply,    setReply]   = useState('')
  const [sending,  setSending] = useState(false)
  const [updating, setUpdating]= useState(false)
  const [detail,   setDetail]  = useState(null)
  const [loading,  setLoading] = useState(false)

  useEffect(() => {
    if (!ticket) return
    setDetail(null)
    setLoading(true)
    api.get(`/helpcenter/admin/tickets/${ticket.ticket_id}`)
      .then(res => setDetail(res.data.ticket))
      .catch(() => setDetail(ticket))
      .finally(() => setLoading(false))
  }, [ticket?.ticket_id])

  if (!ticket) return null
  const d  = detail || ticket
  const bg = avatarBg(d.user_name || d.user_phone || '')

  const handleStatusChange = async (newStatus) => {
    setUpdating(true)
    try {
      await api.patch(`/helpcenter/admin/tickets/${d.ticket_id}/status`, { status: newStatus })
      setDetail(prev => ({ ...prev, status: newStatus }))
      onUpdated(d.ticket_id, { status: newStatus })
    } catch { alert('Failed to update status') }
    finally { setUpdating(false) }
  }

  const handleReply = async (e) => {
    e.preventDefault()
    if (!reply.trim()) return
    setSending(true)
    try {
      const res = await api.post(`/helpcenter/admin/tickets/${d.ticket_id}/reply`, { message: reply.trim() })
      setDetail(prev => ({ ...prev, messages: [...(prev.messages||[]), res.data.message] }))
      setReply('')
      if (d.status === 'open') handleStatusChange('in_progress')
    } catch { alert('Failed to send reply') }
    finally { setSending(false) }
  }

  const nextStatuses = {
    open:        ['in_progress', 'resolved', 'closed'],
    in_progress: ['resolved', 'closed'],
    resolved:    ['closed', 'open'],
    closed:      ['open'],
  }[d.status?.toLowerCase()] || []

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.18)', zIndex:200 }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:540, background:'#fff', zIndex:201, display:'flex', flexDirection:'column', boxShadow:'-4px 0 40px rgba(0,0,0,0.12)', animation:'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'flex-start', gap:12 }}>
          <div style={{ width:42, height:42, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, fontWeight:800, flexShrink:0 }}>
            {initials(d.user_name || d.user_phone)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:800, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.subject}</div>
            <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>
              {d.user_name || d.user_phone || '—'} · #{d.ticket_id} · {fmt(d.created_at)}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:5 }}>
            <StatusBadge status={d.status} />
            <PriorityBadge priority={d.priority} />
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', marginLeft:4 }}>×</div>
        </div>

        {/* Meta strip */}
        <div style={{ padding:'10px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', gap:20, flexWrap:'wrap' }}>
          {[
            ['Category', d.category || '—'],
            ['Role',     d.user_role || '—'],
            ['Email',    d.user_email || '—'],
          ].map(([k,v]) => (
            <div key={k}>
              <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em' }}>{k}</div>
              <div style={{ fontSize:12, fontWeight:600, marginTop:2 }}>{v}</div>
            </div>
          ))}
          <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
            {nextStatuses.map(s => {
              const st = ts(s)
              return (
                <button key={s} onClick={() => handleStatusChange(s)} disabled={updating}
                  style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${st.color}22`, background:st.bg, color:st.color, fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  → {ts(s).label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Messages thread */}
        <div style={{ flex:1, overflowY:'auto', padding:'18px 22px', display:'flex', flexDirection:'column', gap:12 }}>
          {loading && <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'24px 0' }}>Loading…</div>}

          {/* Original message */}
          {!loading && (
            <div style={{ background:'#f9f9f7', borderRadius:10, padding:'14px 16px', border:'1px solid #e9e9e4' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ width:26, height:26, borderRadius:6, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800 }}>
                  {initials(d.user_name || d.user_phone)}
                </div>
                <span style={{ fontSize:12, fontWeight:700 }}>{d.user_name || 'User'}</span>
                <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginLeft:'auto' }}>{fmtTime(d.created_at)}</span>
              </div>
              <div style={{ fontSize:13, color:'#374151', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{d.message || d.description}</div>
            </div>
          )}

          {/* Replies */}
          {!loading && d.messages?.map((msg, i) => {
            const isAdmin = msg.sender === 'admin'
            return (
              <div key={msg.message_id || i}
                style={{ background:isAdmin?'#111317':'#f9f9f7', borderRadius:10, padding:'14px 16px', border:`1px solid ${isAdmin?'transparent':'#e9e9e4'}`, marginLeft:isAdmin?'40px':0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <div style={{ width:24, height:24, borderRadius:6, background:isAdmin?'#e8440a':'#e9e9e4', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:isAdmin?'#fff':'#111317' }}>
                    {isAdmin ? 'A' : initials(d.user_name)}
                  </div>
                  <span style={{ fontSize:12, fontWeight:700, color:isAdmin?'#fff':'#111317' }}>
                    {isAdmin ? (msg.admin_name || 'Support') : (d.user_name || 'User')}
                  </span>
                  <span style={{ fontSize:10, color:isAdmin?'rgba(255,255,255,0.4)':'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginLeft:'auto' }}>
                    {fmtTime(msg.created_at)}
                  </span>
                </div>
                <div style={{ fontSize:13, color:isAdmin?'rgba(255,255,255,0.85)':'#374151', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{msg.message}</div>
              </div>
            )
          })}
        </div>

        {/* Reply box */}
        {!['closed','resolved'].includes(d.status?.toLowerCase()) && (
          <form onSubmit={handleReply} style={{ padding:'14px 22px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10 }}>
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Type your reply…"
              rows={3}
              style={{ flex:1, borderRadius:8, border:'1px solid #e9e9e4', padding:'10px 13px', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", resize:'none', outline:'none', lineHeight:1.5 }}
            />
            <button type="submit" disabled={sending || !reply.trim()}
              style={{ height:42, alignSelf:'flex-end', padding:'0 18px', borderRadius:8, border:'none', background:sending||!reply.trim()?'#f0c4b4':'#e8440a', color:'#fff', fontSize:12, fontWeight:700, cursor:sending||!reply.trim()?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>
              {sending ? 'Sending…' : '↑ Send'}
            </button>
          </form>
        )}
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function HelpCenter() {
  const [tab,          setTab]          = useState('tickets')  // 'tickets' | 'faqs'
  const [tickets,      setTickets]      = useState([])
  const [faqs,         setFaqs]         = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [catFilter,    setCatFilter]    = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total:0, open:0, in_progress:0, resolved:0, avg_response:0 })
  const [selected,     setSelected]     = useState(null)
  const [faqModal,     setFaqModal]     = useState(null)

  const PER_PAGE = 20

  // ── Fetch tickets ──
  const fetchTickets = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search        && { search }),
        ...(statusFilter  && { status: statusFilter }),
        ...(catFilter     && { category: catFilter }),
      })
      const res = await api.get(`/helpcenter/admin/tickets?${params}`)
      setTickets(res.data.tickets    || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats        || { total:0, open:0, in_progress:0, resolved:0, avg_response:0 })
    } catch {
      setError('Failed to load — showing mock data')
      const statuses = ['open','open','in_progress','resolved','closed']
      const cats     = ['Order','Payment','Delivery','Account','Technical']
      const mock = Array.from({ length: 15 }, (_, i) => ({
        ticket_id:   1000 + i,
        subject:     ['Order not delivered', 'Payment failed', 'Cannot login', 'Wrong item received', 'Refund request'][i%5],
        category:    cats[i%5],
        status:      statuses[i%5],
        priority:    ['medium','high','low','urgent','medium'][i%5],
        user_name:   i%4===0 ? null : `User ${i+1}`,
        user_phone:  `078${String(i+1).padStart(7,'0')}`,
        user_role:   ['buyer','seller','buyer','driver','buyer'][i%5],
        message:     'I need help with my issue. Please assist me as soon as possible.',
        created_at:  new Date(Date.now()-i*86400000*1.5).toISOString(),
        messages:    [],
      }))
      setTickets(mock)
      setTotalPages(3); setTotalCount(60)
      setStats({ total:60, open:22, in_progress:14, resolved:18, avg_response:4.2 })
    } finally { setLoading(false) }
  }, [page, search, statusFilter, catFilter])

  // ── Fetch FAQs ──
  const fetchFaqs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/helpcenter/admin/faqs')
      setFaqs(res.data.faqs || [])
    } catch {
      setFaqs([
        { faq_id:1, category:'Order',   question:'How do I track my order?',     answer:'Go to Orders tab → tap the order → you will see real-time delivery status.',               active:true  },
        { faq_id:2, category:'Payment', question:'What payment methods are accepted?', answer:'We accept MTN MoMo, Airtel Money, and iPay/Irembo.',                                  active:true  },
        { faq_id:3, category:'Account', question:'How do I become a seller?',     answer:'Go to Settings → Register as Seller → fill the form. Approval takes 24–48 hours.',       active:true  },
        { faq_id:4, category:'Delivery','question':'What is the delivery fee?',   answer:'Delivery fees vary by zone. Check the checkout screen for your exact delivery cost.',    active:true  },
        { faq_id:5, category:'Technical','question':'Why can I not upload a reel?','answer':'Make sure your video is under 60s and under 300MB. Supported formats: MP4, MOV.',     active:false },
      ])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (tab === 'tickets') fetchTickets()
    else fetchFaqs()
  }, [tab, fetchTickets, fetchFaqs])

  useEffect(() => { setPage(1) }, [search, statusFilter, catFilter])

  const handleTicketUpdated = (ticketId, updates) => {
    setTickets(prev => prev.map(t => t.ticket_id === ticketId ? { ...t, ...updates } : t))
  }

  const handleFaqSaved = (faq) => {
    setFaqs(prev => {
      const exists = prev.find(f => f.faq_id === faq.faq_id)
      return exists ? prev.map(f => f.faq_id === faq.faq_id ? faq : f) : [...prev, faq]
    })
  }

  const handleFaqDelete = async (faq) => {
    if (!window.confirm(`Delete FAQ: "${faq.question}"?`)) return
    try {
      await api.delete(`/helpcenter/admin/faqs/${faq.faq_id}`)
      setFaqs(prev => prev.filter(f => f.faq_id !== faq.faq_id))
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete') }
  }

  const faqsByCategory = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'General'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(faq)
    return acc
  }, {})

  return (
    <div style={{ padding:'28px 32px 60px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
      <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>Help Center</div>
          <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>Manage support tickets and FAQ articles.</div>
        </div>
        {tab === 'faqs' && (
          <button onClick={() => setFaqModal({})}
            style={{ height:40, padding:'0 20px', borderRadius:9, border:'none', background:'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(232,68,10,0.25)' }}>
            + New FAQ
          </button>
        )}
      </div>

      {/* Stats (tickets only) */}
      {tab === 'tickets' && (
        <div style={{ display:'flex', gap:14, marginBottom:24 }}>
          <StatCard label="Total Tickets"  value={stats.total.toLocaleString()}                         />
          <StatCard label="Open"           value={stats.open.toLocaleString()}        accent="#1d4ed8"  />
          <StatCard label="In Progress"    value={stats.in_progress.toLocaleString()} accent="#b45309"  />
          <StatCard label="Resolved"       value={stats.resolved.toLocaleString()}    accent="#15803d"  />
          <StatCard label="Avg Response"   value={`${Number(stats.avg_response||0).toFixed(1)}h`}       />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:16, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:5, width:'fit-content' }}>
        {[['tickets','🎫 Tickets'],['faqs','❓ FAQs']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'7px 20px', borderRadius:7, border:'none', background:tab===id?'#111317':'transparent', color:tab===id?'#fff':'#9a9a94', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", transition:'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TICKETS TAB ── */}
      {tab === 'tickets' && (
        <>
          {/* Toolbar */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'12px 16px', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:200, background:'#f6f6f3', border:'1px solid #e9e9e4', borderRadius:7, padding:'7px 12px' }}>
              <span style={{ fontSize:14, color:'#9a9a94' }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by subject, user or phone…"
                style={{ border:'none', outline:'none', background:'transparent', fontSize:13, color:'#111317', flex:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
              {search && <span onClick={() => setSearch('')} style={{ cursor:'pointer', color:'#9a9a94', fontSize:16 }}>×</span>}
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={SS.select}>
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={SS.select}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={fetchTickets} style={{ height:36, width:36, borderRadius:7, border:'1px solid #e9e9e4', background:'#fff', cursor:'pointer', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center' }}>↻</button>
            <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap' }}>{totalCount.toLocaleString()} tickets</div>
          </div>

          {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 16px', marginBottom:14, fontSize:13, color:'#b91c1c' }}>⚠ {error}</div>}

          {/* Tickets table */}
          <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'0.6fr 2fr 1fr 1fr 0.8fr 1fr 0.9fr', padding:'10px 20px', borderBottom:'1px solid #e9e9e4', background:'#f9f9f7' }}>
              {['#','Subject','User','Category','Priority','Status','Date'].map(h => (
                <div key={h} style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {loading ? (
              <div style={{ padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>Loading tickets…</div>
            ) : tickets.length === 0 ? (
              <div style={{ padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>No tickets found.</div>
            ) : (
              tickets.map((t, i) => (
                <div key={t.ticket_id} onClick={() => setSelected(t)}
                  style={{ display:'grid', gridTemplateColumns:'0.6fr 2fr 1fr 1fr 0.8fr 1fr 0.9fr', padding:'13px 20px', borderBottom:i<tickets.length-1?'1px solid #f3f3f0':'none', cursor:'pointer', transition:'background 0.12s', alignItems:'center', background: t.status==='open' ? '#fffffe' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background='#fafaf8'}
                  onMouseLeave={e => e.currentTarget.style.background=t.status==='open'?'#fffffe':'transparent'}
                >
                  <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>#{t.ticket_id}</div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight: t.status==='open'?700:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:7 }}>
                      {t.status === 'open' && <span style={{ width:7, height:7, borderRadius:'50%', background:'#3b82f6', display:'inline-block', flexShrink:0 }} />}
                      {t.subject}
                    </div>
                    <div style={{ fontSize:11, color:'#9a9a94', marginTop:1 }}>{t.messages?.length > 0 ? `${t.messages.length} replies` : 'No replies yet'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.user_name || 'Anonymous'}</div>
                    <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{t.user_role || '—'}</div>
                  </div>
                  <div style={{ fontSize:12, color:'#374151' }}>{t.category || '—'}</div>
                  <div><PriorityBadge priority={t.priority} /></div>
                  <div><StatusBadge status={t.status} /></div>
                  <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{fmt(t.created_at)}</div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
              <div style={{ display:'flex', gap:6 }}>
                <PagBtn label="← Prev" disabled={page===1}           onClick={() => setPage(p=>p-1)} />
                {Array.from({ length:Math.min(5,totalPages) }, (_,i) => {
                  const p = Math.max(1, Math.min(page-2, totalPages-4)) + i
                  return <PagBtn key={p} label={p} active={p===page} onClick={() => setPage(p)} />
                })}
                <PagBtn label="Next →" disabled={page===totalPages}  onClick={() => setPage(p=>p+1)} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── FAQs TAB ── */}
      {tab === 'faqs' && (
        loading
          ? <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'60px 0' }}>Loading FAQs…</div>
          : Object.keys(faqsByCategory).length === 0
            ? (
              <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'60px 32px', textAlign:'center' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>❓</div>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>No FAQs yet</div>
                <div style={{ fontSize:13, color:'#9a9a94', marginBottom:20 }}>Create your first FAQ article.</div>
                <button onClick={() => setFaqModal({})}
                  style={{ height:40, padding:'0 20px', borderRadius:8, border:'none', background:'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>+ New FAQ</button>
              </div>
            )
            : Object.entries(faqsByCategory).map(([category, items]) => (
                <div key={category} style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono',monospace", marginBottom:10 }}>{category}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {items.map(faq => (
                      <div key={faq.faq_id} style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:12 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                            <div style={{ fontSize:13, fontWeight:700 }}>{faq.question}</div>
                            {!faq.active && (
                              <span style={{ fontSize:9, fontWeight:700, background:'#fef2f2', color:'#b91c1c', padding:'2px 6px', borderRadius:3, fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Hidden</span>
                            )}
                          </div>
                          <div style={{ fontSize:12, color:'#6b7280', lineHeight:1.6 }}>{faq.answer}</div>
                        </div>
                        <div style={{ display:'flex', gap:7, flexShrink:0 }}>
                          <button onClick={() => setFaqModal(faq)}
                            style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #e9e9e4', background:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Edit</button>
                          <button onClick={() => handleFaqDelete(faq)}
                            style={{ padding:'4px 10px', borderRadius:6, border:'none', background:'#fef2f2', color:'#b91c1c', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Del</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
      )}

      {/* Modals & Drawers */}
      <TicketDrawer ticket={selected} onClose={() => setSelected(null)} onUpdated={handleTicketUpdated} />
      {faqModal !== null && (
        <FaqModal faq={Object.keys(faqModal).length ? faqModal : null} onClose={() => setFaqModal(null)} onSaved={handleFaqSaved} />
      )}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth:34, height:34, borderRadius:7, border:'1px solid #e9e9e4', background:active?'#111317':'#fff', color:active?'#fff':disabled?'#9a9a94':'#111317', fontSize:12, fontWeight:700, cursor:disabled?'not-allowed':'pointer', padding:'0 10px', fontFamily:"'Plus Jakarta Sans',sans-serif", opacity:disabled?0.4:1 }}>
      {label}
    </button>
  )
}